import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ConfirmVariant = 'danger' | 'default';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface ConfirmDialogProps {
  open: boolean;
  options: ConfirmOptions | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({ open, options, onConfirm, onCancel }: ConfirmDialogProps) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const titleId = useId();
  const descId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => confirmRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (typeof document === 'undefined') return null;

  const variant = options?.variant ?? 'default';
  const isDanger = variant === 'danger';
  const Icon = isDanger ? AlertTriangle : Info;

  return createPortal(
    <AnimatePresence>
      {open && options && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">
          <motion.button
            type="button"
            aria-label={options.cancelLabel ?? t('common.cancel')}
            className="absolute inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.15 }}
            onClick={onCancel}
          />
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-900/10 dark:border-lc-border dark:bg-lc-surface dark:shadow-black/40"
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              aria-hidden
              className={`absolute inset-x-0 top-0 h-1 ${
                isDanger
                  ? 'bg-gradient-to-r from-red-500 to-orange-400'
                  : 'bg-gradient-to-r from-brand-purple via-brand-pink to-brand-orange'
              }`}
            />
            <div className="p-6 pt-7">
              <div className="flex gap-4">
                <span
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                    isDanger
                      ? 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400'
                      : 'bg-gradient-to-br from-brand-purple to-brand-pink text-white shadow-sm shadow-brand-pink/30'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-lc-text">
                    {options.title}
                  </h2>
                  <p id={descId} className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-lc-muted">
                    {options.message}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={onCancel} className="btn-secondary">
                  {options.cancelLabel ?? t('common.cancel')}
                </button>
                <button
                  ref={confirmRef}
                  type="button"
                  onClick={onConfirm}
                  className={isDanger ? 'btn-danger' : 'btn-primary'}
                >
                  {options.confirmLabel ?? t('common.confirm')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
